import React from "react";
import { AlertCircle, CheckCircle2, Filter, Loader2, Tag } from "lucide-react";
import { Card, CardContent } from "@/components/ui";
import { cn } from "@/lib/utils";
import { formatProductType } from "@/lib/formatProductType";
import type { AttributeReviewFiltersProps } from "./AttributeReviewFilters.types";
import {
  countBadgeDefault,
  countBadgeSelected,
  filterButtonBase,
  filterButtonDefault,
  filterButtonSelected,
} from "./AttributeReviewFilters.styles";

export const AttributeReviewFilters: React.FC<AttributeReviewFiltersProps> = ({
  marketplaces,
  productTypes,
  selectedMarketplace,
  selectedProductType,
  onMarketplaceSelect,
  onProductTypeSelect,
  context,
  needsReview,
  sessionsLoading,
}) => {
  const completedCount = context.approved;
  // Total comes from the server payload (every field on the canonical model),
  // while Approved / Needs Review reflect what the FE renders after filtering
  // out marketplace-specific and other excluded statuses. Surface that gap
  // explicitly so the three lines actually add up to Total.
  const otherCount = Math.max(
    0,
    context.totalAttributes - completedCount - needsReview,
  );

  return (
    <div className="sticky top-24 space-y-6 max-h-[calc(100vh-8rem)] overflow-y-auto pb-10">
      <Card>
        <CardContent className="p-4">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
            <Filter className="w-4 h-4" />
            Marketplace
          </h3>
          <div className="space-y-2 max-h-[30vh] overflow-y-auto pr-2">
            {marketplaces.length === 0 && !sessionsLoading && (
              <p className="text-xs text-slate-500 dark:text-slate-400">
                No marketplaces
              </p>
            )}
            {marketplaces.map((mp) => (
              <button
                key={mp.id}
                data-testid={`filter-mp-${mp.id}`}
                onClick={() => onMarketplaceSelect(mp.name)}
                className={cn(
                  filterButtonBase,
                  "capitalize",
                  selectedMarketplace === mp.name
                    ? filterButtonSelected
                    : filterButtonDefault,
                )}
              >
                <span className="font-medium">{mp.name}</span>
                <span className={countBadgeDefault}>{mp.count}</span>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
            <Filter className="w-4 h-4" />
            Product Type
          </h3>
          <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-2">
            {productTypes.length === 0 && !sessionsLoading && (
              <p className="text-xs text-slate-500 dark:text-slate-400">
                No product types
              </p>
            )}
            {productTypes.map((pt) => {
              // formatProductType prettifies normal codes but leaves numeric
              // category ids (Meesho/Coupang) unchanged — only then fall back
              // to the server's readable displayName. Other marketplaces keep
              // their existing formatted label untouched.
              const formatted = formatProductType(pt.name) || pt.name;
              const ptLabel =
                /^\d+$/.test(pt.name.trim()) && formatted === pt.name
                  ? pt.displayName || pt.name
                  : formatted;

              return (
                <button
                  key={pt.id}
                  data-testid={`filter-pt-${pt.id}`}
                  onClick={() => onProductTypeSelect(pt.name)}
                  className={cn(
                    filterButtonBase,
                    selectedProductType === pt.name
                      ? filterButtonSelected
                      : filterButtonDefault,
                  )}
                >
                  <span className="font-medium text-left">{ptLabel}</span>
                  <span
                    className={cn(
                      selectedProductType === pt.name
                        ? countBadgeSelected
                        : countBadgeDefault,
                    )}
                  >
                    {pt.count}
                  </span>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
            Review Progress
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500 dark:text-slate-400">
                Total
              </span>
              <span className="text-sm font-bold text-slate-900 dark:text-slate-100">
                {sessionsLoading ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />
                ) : (
                  context.totalAttributes
                )}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                Approved
              </span>
              <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
                {sessionsLoading ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-400" />
                ) : (
                  completedCount
                )}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5 text-amber-500" />
                To review
              </span>
              <span className="text-sm font-bold text-amber-600 dark:text-amber-400">
                {sessionsLoading ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-amber-400" />
                ) : (
                  needsReview
                )}
              </span>
            </div>
            {!sessionsLoading && otherCount > 0 && (
              <div
                className="flex items-center justify-between"
                title="Fields routed to Marketplace-Specific Review or otherwise excluded from the main queue."
              >
                <span className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-1">
                  <Tag className="w-3.5 h-3.5 text-sky-500" />
                  Marketplace-specific
                </span>
                <span className="text-sm font-bold text-sky-600 dark:text-sky-400">
                  {otherCount}
                </span>
              </div>
            )}
          </div>
          <div className="mt-4">
            <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full transition-all duration-500"
                style={{
                  width: `${
                    context.totalAttributes > 0
                      ? (completedCount / context.totalAttributes) * 100
                      : 0
                  }%`,
                }}
              />
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 text-center">
              {context.totalAttributes > 0
                ? `${Math.round(
                    (completedCount / context.totalAttributes) * 100,
                  )}% Complete`
                : "No fields"}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
